import { Button } from "../../../catalyst-components/button"
import { Field } from "../../../catalyst-components/fieldset"
import { useContext, useRef, memo, useState, useEffect } from "react";
import { BaseContext } from "../../../shared/base-context";
import { MainRoutes, EvergreenEntryPoints } from "../../../shared/models";
import { useNavigate } from "react-router-dom";
import { Textarea } from "../../../catalyst-components/textarea";
import { useMutation } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { Id } from "../../../convex/_generated/dataModel";
import { useStableQuery } from "../../../shared/hooks/useStableQuery";
import { useTranslation } from 'react-i18next';

export interface ChatInputProps {
  onMessageSubmit: (message: string) => void;
  responseInProgress: boolean;
  isComplete?: boolean;
  testing?: boolean;
  onUserTyping?: () => void;
  showInput?: boolean;
  sessionId: Id<"sessions">;
  assistantId: Id<"assistants">;
}

export const ChatInput = memo(({
  onMessageSubmit,
  responseInProgress,
  isComplete,
  onUserTyping,
  showInput,
  sessionId,
  assistantId
}: ChatInputProps) => {
  const { t } = useTranslation();
  const { debug, convexUserId } = useContext(BaseContext);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [messageText, setMessageText] = useState("");
  const completeSession = useMutation(api.sessions.completeSession);
  const createSession = useMutation(api.sessions.startSession);
  const injectContext = useMutation(api.evergreen_chat.injectContext);
  
  // Get assistant info for the session name
  const assistant = useStableQuery(
    api.assistants.getById,
    assistantId ? { id: assistantId } : "skip"
  );
  
  const handleFinish = () => {
    navigate(MainRoutes.Progress);
  }

  const handleMoreOnThis = async () => {
    if (!convexUserId) return;
    
    const sessionName = assistant?.display_name || assistant?.name || "the session";
    
    // Create entry point for session completion
    const entryPoint = EvergreenEntryPoints.sessionComplete(
      sessionName,
      "their current skill"
    );
    
    // Inject context and trigger auto-response
    await injectContext({
      user_id: convexUserId as Id<"users">,
      context_id: entryPoint.contextId,
      system_message: entryPoint.systemMessage,
      auto_submit_prompt: entryPoint.autoSubmitPrompt,
    });
    
    navigate(MainRoutes.MyChat);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (messageText.trim()) {
      onMessageSubmit(messageText);
      setMessageText("");
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessageText(e.target.value);
    // Call onUserTyping when user starts typing
    if (onUserTyping && e.target.value.length > 0) {
      onUserTyping();
    }
  };

  // Auto-resize textarea based on content
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;
    }
  }, [messageText]);

  useEffect(() => {
    if (!responseInProgress) {
      inputRef.current?.focus();
    }
  }, [responseInProgress]);

  const handleStartOver = async () => {
    await completeSession({
      sessionId: sessionId
   });

   const newSession = await createSession({
       assistant_id: assistantId,
       user_id: convexUserId
   });

   navigate(`${MainRoutes.Chat}?sessionId=${newSession.session_id}&testing=true`);
  };

  return (
    <form className="pb-[calc(1rem+env(safe-area-inset-bottom))] px-4 fixed bottom-0 left-0 right-0 bg-background border-t border-border/50 pt-4" onSubmit={handleSubmit}>
      <div className="max-w-4xl mx-auto">
        {debug && (
          <div className="text-xs text-center text-primary-foreground mb-2 bg-primary/80 p-2 rounded-lg flex items-center justify-center gap-4">
            <span>Completion: {isComplete ? 'yes' : 'no'}</span>
            <span>|</span>
            <span>Response In Progress: {responseInProgress ? 'yes' : 'no'}</span>
            <Button color="secondary" className="rounded-full py-1 px-3 text-xs" onClick={handleStartOver}>
              Start over
            </Button>
          </div>
        )}
        {!showInput && isComplete && (
          <div className="text-sm text-center text-muted-foreground mb-4 p-4 bg-card rounded-xl border border-border/50">
            <p className="mb-3">{t('chatInput.heckYea')}</p>
            <div className="flex justify-center gap-3">
              <Button color="primary" className="rounded-full px-6" onClick={handleFinish}>
                {t('chatInput.nextSession')}
              </Button>
              <Button color="secondary" className="rounded-full px-6" onClick={handleMoreOnThis}>
                {t('chatInput.moreOnThis')}
              </Button>
            </div>
          </div>
        )}
        {showInput && (
          <Field>

          <section className="flex gap-3">
            <Textarea
              ref={inputRef}
              name="user_input"
              value={messageText}
              onChange={handleInputChange}
              disabled={responseInProgress}
              placeholder={t('chatInput.placeholder')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
              style={{
                minHeight: '3rem',
                maxHeight: '14rem',
                resize: 'none',
                overflowY: 'auto'
              }}
              className="flex-1"
            />
            <Button
              color="primary"
              type="submit"
              className="h-12 w-12 min-h-12 min-w-12 max-h-12 max-w-12 flex-shrink-0 self-end rounded-full p-0 flex items-center justify-center"
              disabled={!messageText || responseInProgress}
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5" />
              </svg>
            </Button>
            {isComplete && (
              <Button
                color="secondary"
                className="rounded-full self-end"
                onClick={handleFinish}
              >
                {t('chatInput.finish')}
              </Button>
              )}
            </section>
          </Field>
        )}
      </div>
    </form>
  )
});

ChatInput.displayName = 'ChatInput';